import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft } from 'react-icons/fi';
import { useWatchHistory, HistoryItem } from '../../contexts/WatchHistoryContext';
import { useWatchProgress } from '../../contexts/WatchProgressContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { MovieDetailsEntry } from '../MovieDetails/MovieDetails';
import { SeriesDetailsEntry } from '../SeriesDetails/SeriesDetails';
import './ContinueWatching.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContinueWatchingProps {
  isActive?: boolean;
  onBack?: () => void;
  onOpenMovieDetails?: (entry: MovieDetailsEntry) => void;
  onOpenSeriesDetails?: (entry: SeriesDetailsEntry) => void;
}

// ─── Layout constants ─────────────────────────────────────────────────────────

type FocusSection = 'back' | 'movies' | 'series';

const COLS = 6;

// ─── Card component ───────────────────────────────────────────────────────────

interface CardProps {
  item: HistoryItem;
  progressPct: number;
  focused: boolean;
  onClick: () => void;
  cellRef?: (el: HTMLDivElement | null) => void;
}

const RING_RADIUS = 14;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ≈ 87.96

const HistoryCard: React.FC<CardProps> = ({ item, progressPct, focused, onClick, cellRef }) => {
  const style: React.CSSProperties = item.posterUrl
    ? { backgroundImage: `url(${item.posterUrl})` }
    : {};

  const pct = Math.min(100, Math.max(0, progressPct));
  const offset = RING_CIRCUMFERENCE * (1 - pct / 100);

  return (
    <div
      ref={cellRef}
      className={`cw__card-cell${focused ? ' cw__card-cell--focused' : ''}`}
      onClick={onClick}
      tabIndex={-1}
    >
      <div className="cw__card" style={style}>
        {!item.posterUrl && (
          <div className="cw__card-noimg">{item.name}</div>
        )}
        <div className="cw__card-title">{item.name}</div>
        {pct > 0 && item.type === 'movie' && (
          <div className="cw__progress-ring">
            <svg width="36" height="36" viewBox="0 0 36 36">
              <circle
                cx="18" cy="18" r={RING_RADIUS}
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="3"
              />
              <circle
                cx="18" cy="18" r={RING_RADIUS}
                fill="none"
                stroke="#f5a623"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={offset}
                transform="rotate(-90 18 18)"
              />
              <text
                x="18" y="18"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="9"
                fill="#fff"
                fontFamily="var(--font-roboto, sans-serif)"
                fontWeight="600"
              >
                {Math.round(pct)}%
              </text>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const ContinueWatching: React.FC<ContinueWatchingProps> = ({
  isActive = true,
  onBack,
  onOpenMovieDetails,
  onOpenSeriesDetails,
}) => {
  const { t } = useTranslation();
  const { getMovieHistory, getSeriesHistory } = useWatchHistory();
  const { getProgress } = useWatchProgress();
  const { isKidsMode } = useParentalControls();

  // Stale-while-revalidate: re-fetch progress from server on mount
  const { refreshFromServer: refreshProgress } = useWatchProgress();
  useEffect(() => { refreshProgress(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [focusSection, setFocusSection] = useState<FocusSection>('movies');
  const [focusedMovieIdx, setFocusedMovieIdx] = useState(0);
  const [focusedSeriesIdx, setFocusedSeriesIdx] = useState(0);

  // Scroll refs
  const contentRef = useRef<HTMLDivElement>(null);
  const movieCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const seriesCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollFocusedIntoView = useCallback((card: HTMLDivElement | null) => {
    const container = contentRef.current;
    if (!card || !container) return;
    const cardRect = card.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const cardRelTop = cardRect.top - containerRect.top + container.scrollTop;
    const cardRelBottom = cardRelTop + cardRect.height;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;
    if (cardRelBottom > visibleBottom - 12) {
      container.scrollTop = cardRelBottom - container.clientHeight + 12;
    } else if (cardRelTop < visibleTop + 12) {
      container.scrollTop = cardRelTop - 12;
    }
  }, []);

  useEffect(() => {
    if (focusSection === 'movies') scrollFocusedIntoView(movieCardRefs.current[focusedMovieIdx]);
  }, [focusedMovieIdx, focusSection, scrollFocusedIntoView]);

  useEffect(() => {
    if (focusSection === 'series') scrollFocusedIntoView(seriesCardRefs.current[focusedSeriesIdx]);
  }, [focusedSeriesIdx, focusSection, scrollFocusedIntoView]);

  // ─── Derived lists ──────────────────────────────────────────────────────
  const movieItems = useMemo(() => {
    return getMovieHistory()
      .filter((item) => !isKidsMode || !!item.isKids)
      .filter((item) => {
        const prog = getProgress(item.streamId, 'movie');
        if (!prog) return false;
        const pct = (prog.position / prog.duration) * 100;
        return prog.position >= 5 && pct < 95;
      });
  }, [getMovieHistory, getProgress, isKidsMode]);

  const seriesItems = useMemo(() => {
    return getSeriesHistory()
      .filter((item) => !isKidsMode || !!item.isKids);
  }, [getSeriesHistory, isKidsMode]);

  // Auto-focus first non-empty section
  useEffect(() => {
    if (!isActive) return;
    if (movieItems.length > 0) {
      setFocusSection('movies');
      setFocusedMovieIdx(0);
    } else if (seriesItems.length > 0) {
      setFocusSection('series');
      setFocusedSeriesIdx(0);
    } else {
      setFocusSection('movies');
    }
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Navigation helpers ─────────────────────────────────────────────────
  const openMovie = useCallback((item: HistoryItem) => {
    const prog = getProgress(item.streamId, 'movie');
    onOpenMovieDetails?.({
      tmdbId: parseInt(item.id, 10),
      title: item.name,
      posterUrl: item.posterUrl,
      backdropUrl: item.backdropUrl,
      vodStreamId: item.streamId,
      isKids: item.isKids,
    });
  }, [getProgress, onOpenMovieDetails]);

  const openSeries = useCallback((item: HistoryItem) => {
    onOpenSeriesDetails?.({
      tmdbId: parseInt(item.id, 10),
      title: item.name,
      posterUrl: item.posterUrl,
      backdropUrl: item.backdropUrl,
      seriesStreamId: item.streamId,
      isKids: item.isKids,
    });
  }, [onOpenSeriesDetails]);

  // ─── State ref for keyboard handler ────────────────────────────────────
  const stateRef = useRef({ focusSection, focusedMovieIdx, focusedSeriesIdx, movieItems, seriesItems });
  useEffect(() => {
    stateRef.current = { focusSection, focusedMovieIdx, focusedSeriesIdx, movieItems, seriesItems };
  });

  // ─── Keyboard ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const { focusSection: sec, focusedMovieIdx: mi, focusedSeriesIdx: si, movieItems: mvs, seriesItems: srs } = stateRef.current;
      const { key } = e;

      if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack') {
        e.preventDefault();
        onBack?.();
        return;
      }

      if (sec === 'back') {
        if (key === 'ArrowDown') { e.preventDefault(); setFocusSection(mvs.length ? 'movies' : 'series'); return; }
        if (key === 'Enter' || key === ' ') { e.preventDefault(); onBack?.(); return; }
        return;
      }

      if (sec === 'movies') {
        const total = mvs.length;
        if (key === 'ArrowUp') {
          e.preventDefault();
          const row = Math.floor(mi / COLS);
          if (row === 0) { setFocusSection('back'); } else { setFocusedMovieIdx(Math.max(0, mi - COLS)); }
          return;
        }
        if (key === 'ArrowDown') {
          e.preventDefault();
          if (mi + COLS < total) {
            setFocusedMovieIdx(Math.min(total - 1, mi + COLS));
          } else if (srs.length > 0) {
            setFocusSection('series');
            setFocusedSeriesIdx(0);
          }
          return;
        }
        if (key === 'ArrowLeft') { e.preventDefault(); setFocusedMovieIdx((prev) => Math.max(0, prev - 1)); return; }
        if (key === 'ArrowRight') { e.preventDefault(); setFocusedMovieIdx((prev) => Math.min(total - 1, prev + 1)); return; }
        if ((key === 'Enter' || key === ' ') && total > 0) {
          e.preventDefault();
          openMovie(mvs[mi]);
          return;
        }
        return;
      }

      if (sec === 'series') {
        const total = srs.length;
        if (key === 'ArrowUp') {
          e.preventDefault();
          const row = Math.floor(si / COLS);
          if (row === 0) {
            if (mvs.length > 0) { setFocusSection('movies'); setFocusedMovieIdx(Math.min(mvs.length - 1, mvs.length - 1)); }
            else { setFocusSection('back'); }
          } else {
            setFocusedSeriesIdx(Math.max(0, si - COLS));
          }
          return;
        }
        if (key === 'ArrowDown') {
          e.preventDefault();
          setFocusedSeriesIdx((prev) => Math.min(total - 1, prev + COLS));
          return;
        }
        if (key === 'ArrowLeft') { e.preventDefault(); setFocusedSeriesIdx((prev) => Math.max(0, prev - 1)); return; }
        if (key === 'ArrowRight') { e.preventDefault(); setFocusedSeriesIdx((prev) => Math.min(total - 1, prev + 1)); return; }
        if ((key === 'Enter' || key === ' ') && total > 0) {
          e.preventDefault();
          openSeries(srs[si]);
          return;
        }
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, onBack, openMovie, openSeries]);

  const isEmpty = movieItems.length === 0 && seriesItems.length === 0;

  return (
    <div className="cw">
      <div className="cw__header">
        <button
          type="button"
          className={`cw__back-btn${focusSection === 'back' ? ' cw__back-btn--focused' : ''}`}
          onClick={onBack}
          tabIndex={-1}
          aria-label={t('common.back')}
        >
          <FiArrowLeft size={22} />
        </button>
        <span className="cw__title">{t('continueWatching.title')}</span>
      </div>

      <div className="cw__content" ref={contentRef}>
        {isEmpty ? (
          <div className="cw__empty">{t('continueWatching.empty')}</div>
        ) : (
          <>
            {movieItems.length > 0 && (
              <div className="cw__section">
                <div className={`cw__section-label${focusSection === 'movies' ? ' cw__section-label--focused' : ''}`}>
                  {t('continueWatching.movies')}
                </div>
                <div className="cw__grid">
                  {movieItems.map((item, i) => {
                    const prog = getProgress(item.streamId, 'movie');
                    const pct = prog ? (prog.position / prog.duration) * 100 : 0;
                    return (
                      <HistoryCard
                        key={`${item.type}:${item.id}`}
                        item={item}
                        progressPct={pct}
                        focused={focusSection === 'movies' && focusedMovieIdx === i}
                        onClick={() => openMovie(item)}
                        cellRef={(el) => { movieCardRefs.current[i] = el; }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {seriesItems.length > 0 && (
              <div className="cw__section">
                <div className={`cw__section-label${focusSection === 'series' ? ' cw__section-label--focused' : ''}`}>
                  {t('continueWatching.series')}
                </div>
                <div className="cw__grid">
                  {seriesItems.map((item, i) => {
                    const prog = item.lastEpisodeStreamId ? getProgress(item.lastEpisodeStreamId, 'series_episode') : null;
                    const pct = prog ? (prog.position / prog.duration) * 100 : 0;
                    return (
                      <HistoryCard
                        key={`${item.type}:${item.id}`}
                        item={item}
                        progressPct={pct}
                        focused={focusSection === 'series' && focusedSeriesIdx === i}
                        onClick={() => openSeries(item)}
                        cellRef={(el) => { seriesCardRefs.current[i] = el; }}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
